/** Dates, places and holidays.
 *
 *  Berlin is never stored — only the exceptions are — so the sequence of
 *  periods is derived here by filling the gaps between stays. Public holidays
 *  are computed rather than stored, because they are a function of the year and
 *  the country and a table would only ever be a cache that goes stale.
 */
import type { Stay } from './types'
import { holidayMap, holidaysFor } from './holidays.ts'

const berlinDayFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit',
})
/** The Berlin calendar day a moment falls on, `YYYY-MM-DD` — what every
 *  `iso(new Date())` means by "today". Was `toISOString().slice(0, 10)`, the
 *  UTC day, which is yesterday from midnight until 01:00/02:00 Berlin: the
 *  Year's today ring, its year toggle on 1 Jan and every "today" on the phone
 *  lagged a day in the hours this app is used (a3-03). That one value did two
 *  jobs, "today" and the date arithmetic below; the arithmetic works at UTC
 *  noon, where both answers agree, so it keeps its own cheaper slice. */
export const iso = (d: Date) => berlinDayFmt.format(d)
export const D = (s: string) => new Date(s + 'T12:00:00Z')
export const days = (a: string, b: string) => Math.round((D(b).getTime() - D(a).getTime()) / 86400000)
export const addDays = (s: string, n: number) => new Date(D(s).getTime() + n * 86400000).toISOString().slice(0, 10)

/** The instant the next Berlin day begins, for a screen that must roll over
 *  at midnight rather than at the next reload (a3-04). Berlin's midnight is
 *  22:00Z (summer) or 23:00Z (winter) the evening before; the earlier of the
 *  two that `iso` already calls tomorrow is the answer. Asking `iso` rather
 *  than an offset table keeps both clock-change nights right: they happen at
 *  02:00/03:00, after the midnight in question. */
export function nextBerlinMidnight(now: Date): Date {
  const tomorrow = addDays(iso(now), 1)
  const utc = Date.parse(`${tomorrow}T00:00:00Z`)
  const at = [utc - 2 * 3600_000, utc - 3600_000].find(t => iso(new Date(t)) === tomorrow) ?? utc
  return new Date(at)
}
// Memoised: `toLocaleDateString` with an options bag builds a new
// `Intl.DateTimeFormat` per call, and the result is a pure function of the
// day and the options. Bounded; cleared past 20k entries.
const fmtCache = new Map<string, string>()
export const fmt = (s: string, o: Intl.DateTimeFormatOptions) => {
  const key = `${s}|${JSON.stringify(o)}`
  const hit = fmtCache.get(key)
  if (hit !== undefined) return hit
  // A day that is not one — `undefined` from a row missing the column, a
  // half-written draft — prints nothing rather than "Invalid Date" (a2-46).
  const d = D(s)
  const out = Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { timeZone: 'UTC', ...o })
  if (fmtCache.size >= 20_000) fmtCache.clear()
  fmtCache.set(key, out)
  return out
}
/** Monday-first: 0 Monday … 6 Sunday — the month ribbon's weekday letters
 *  start on Monday, so "today" needs the same convention to find its week. */
export const dow = (s: string) => (D(s).getUTCDay() + 6) % 7

/** `Home2` is time at the profile's second home; what it is called is the
 *  profile's (`secondHomeLabel`), never the place's own name — see `placeName`. */
export type Place = 'Berlin' | 'Home2' | 'Travel'

/** What a period is called wherever one is printed: a trip's own label, the
 *  profile's second-home label for `Home2`, otherwise the place. `Home2` with
 *  no label to read (the profile has not loaded, or never had a second home
 *  and a stay row exists anyway) says "Second home" rather than guessing a
 *  country. */
export function placeName(p: { place: Place; label: string | null }, second: { label: string } | null | undefined): string {
  if (p.place === 'Travel' && p.label) return p.label
  if (p.place === 'Home2') return second?.label.trim() || 'Second home'
  return p.place
}

/**
 * How far ahead the Year's add/edit date pickers reach. Was `timelineLayout.ts`'s,
 * back when the rail's own drawn horizon (WP 5.9) was a shorter, calendar-
 * derived bound and this had to be named apart from it on purpose — the two
 * were one value before that, which is the bug CLAUDE.md opens with. WP 6.5
 * retired the rail; this constant survives it because the date pickers still
 * need a bound and 730 days is still the number Doug agreed to.
 */
export const ADD_MAX_DAYS = 730

/**
 * The earliest day a Year form's date picker offers: 1 January of the year
 * being edited — this year for a new item, the item's own year for an edit
 * of one that started earlier (a 2025 trip stays correctable).
 *
 * Was `today` (a2-26), while the header and the leave counter count the
 * whole calendar year: a trip already taken in September could never be
 * entered, so the numbers above the form could never be made right. The
 * stats count from 1 January, so that is where the forms open from too.
 */
export function yearFormMin(today: string, initial?: string | null): string {
  const from = initial && initial < today ? initial : today
  return `${from.slice(0, 4)}-01-01`
}

export interface Period {
  place: Place
  label: string | null
  provisional: boolean
  /** ISO 3166-1 alpha-2 from the `stays` row, where one stated it. Drives the
   *  flag; a Travel period without one falls back to guessing from the label. */
  country?: string | null
  start: string
  end: string
  /** True for a stretch with nothing in it, so the app can draw it quietly. */
  open?: boolean
  /** The `stays` row this period came from, where one did.
   *
   *  Berlin has none — it is what is left when no stay covers a day, so there
   *  is nothing to delete and the app must not offer to. Everything else can
   *  be removed, which is what Doug wanted: a trip he no longer takes was
   *  unremovable because the period had no way back to its row. */
  stayId?: string
}

/** Berlin's public holidays for a year and the years either side: a stay can
 *  cross New Year. The set both Years mark leave against. */
export const deHolidays = (year: number): Record<string, string> => countryHolidays('DE-BE', year)

/** A country's holidays for a year and the years either side, keyed by day. */
export const countryHolidays = (country: string | null | undefined, year: number): Record<string, string> =>
  ({ ...holidayMap(country, year - 1), ...holidayMap(country, year), ...holidayMap(country, year + 1) })

/** The holidays in `start..end` for the place those days are spent: Berlin's
 *  for Berlin and for a trip, `secondCountry`'s (the profile's) for `Home2`.
 *  A trip is Berlin's calendar, as it always was — leave is counted against
 *  the home calendar. */
export function holidaysIn(start: string, end: string, place: Place, secondCountry?: string | null) {
  const country = place === 'Home2' ? secondCountry : 'DE-BE'
  const out: [string, string][] = []
  for (let y = +start.slice(0, 4); y <= +end.slice(0, 4); y++) {
    for (const h of holidaysFor(country, y)) if (h.on >= start && h.on <= end) out.push([h.on, h.name])
  }
  return out.sort((a, b) => (a[0] < b[0] ? -1 : 1))
}

/**
 * Turn stays into a gapless sequence of periods between two dates.
 *
 * The second home beats Travel on an overlapping day, and Berlin fills everything left.
 *
 * A long Berlin stretch used to be broken at month boundaries, on the theory
 * that a year with nothing planned should be scrollable rather than one
 * enormous block. In practice it produced BERLIN, BERLIN, BERLIN down the
 * screen, one per month, each saying nothing — the repetition was the noise,
 * not the length. One block now, however long: "Berlin until the next time
 * you leave" is the true shape and reads in one line.
 */
export function periods(stays: Stay[], from: string, to: string, longStay = 45): Period[] {
  const home2 = stays.filter(s => s.kind === 'home2')
  const travel = stays.filter(s => s.kind === 'travel')
  const out: Period[] = []
  let cur: Period | null = null

  for (let d = from; d <= to; d = addDays(d, 1)) {
    const i = home2.find(s => d >= s.starts_on && d <= s.ends_on)
    const t = !i ? travel.find(s => d >= s.starts_on && d <= s.ends_on) : undefined
    const v: Omit<Period, 'start' | 'end'> = i
      ? { place: 'Home2', label: i.label, provisional: i.provisional, country: i.country, stayId: i.id }
      : t
        ? { place: 'Travel', label: t.label, provisional: t.provisional, country: t.country, stayId: t.id }
        : { place: 'Berlin', label: null, provisional: false }

    // The stay id joins the run too: two adjacent trips with the same label
    // are two trips, and merging them would offer one delete for both.
    if (cur && cur.place === v.place && cur.label === v.label
        && cur.provisional === v.provisional && cur.stayId === v.stayId) cur.end = d
    else {
      if (cur) out.push(cur)
      cur = { ...v, start: d, end: d }
    }
  }
  if (cur) out.push(cur)

  // `open` marks a long Berlin stretch so the timeline can draw it quietly.
  // It used to be set by the splitter; without one it is a property of the
  // period itself.
  return out.map(p =>
    p.place === 'Berlin' && days(p.start, p.end) >= longStay ? { ...p, open: true } : p
  )
}

/** The period a day falls in, out of an already-computed sequence — the
 *  month ribbon's per-day colour and the day sheet both need "where am I on
 *  this one day" rather than a whole range, and re-deriving `periods()` per
 *  day would repeat the same walk 365 times for one calendar. Callers pass
 *  the same `periods` list they already computed for the visible range;
 *  `undefined` only where `day` falls outside it. */
export function placeOf(periods: Period[], day: string): Period | undefined {
  return periods.find(p => day >= p.start && day <= p.end)
}

/** A holiday on one day, for the place it falls in — `holidaysIn`'s single-day
 *  case, named for what the month ribbon and the day sheet both ask: not "what
 *  holidays are in this range" but "is today one". */
export function holidayOn(day: string, place: Place, secondCountry?: string | null): string | null {
  const hit = holidaysIn(day, day, place, secondCountry)
  return hit.length ? hit[0][1] : null
}
